import React, { useState, useEffect, useRef } from 'react';
import { Handle, Position, useNodes } from '@xyflow/react';
import { message, Alert, Checkbox, Collapse, Popconfirm, Card, Select, Button, Input, Space, Typography, Modal, Tooltip, InputNumber, Tabs, Tag, Spin } from 'antd';
import { 
  DeleteOutlined, 
  PlusOutlined, 
  MinusCircleOutlined, 
  SettingOutlined, 
  InfoCircleOutlined, 
  OpenAIOutlined, 
  GoogleOutlined,
  RobotOutlined,
  PictureOutlined,
  ReloadOutlined,
  WindowsFilled
} from '@ant-design/icons';
import {
  Anthropic,
  DeepSeek,
  Gemini,
  Grok,
  Meta,
  Mistral,
  OpenAI,
  Perplexity,
  Qwen, 
  Nova,
  Nvidia,
  Cohere, 
  Moonshot,
  OpenRouter, 
} from '@lobehub/icons';
import GenericNodeContent from '../common/GenericNodeContent';
import NodeInputHandler from '../common/NodeInputHandler';
import axios from 'axios';
import config from '../../config';

const { Option, OptGroup } = Select;
const { Text } = Typography;
const { TabPane } = Tabs;
const { Panel } = Collapse;

const AIModelNode = ({ id, data, isConnectable }) => {
  const [nodeName, setNodeName] = useState(data.nodeName || `AI Model ${id}`);
  const [imageUrls, setImageUrls] = useState(data.imageUrls || []);
  const [activeTab, setActiveTab] = useState('model');
  const [settings, setSettings] = useState(data.settings || {
    temperature: 1.0,
    top_p: 1.0,
    top_k: 0,
    frequency_penalty: 0.0,
    presence_penalty: 0.0,
    repetition_penalty: 1.0,
  });
  const [content, setContent] = useState(data.content || '');
  const [vectorStores, setVectorStores] = useState([]);
  const [fetchingVectorStores, setFetchingVectorStores] = useState(false);
  const [openaiTools, setOpenaiTools] = useState(data.openaiTools || {
    webSearch: {
        enabled: false,
        contextSize: 'medium',
        location: {
            city: '',
            region: '',
            country: ''
        }
    },
    fileSearch: {
        enabled: false,
        vectorStoreId: '',
        maxResults: 5
    }
  });
  // New state for dynamic OpenRouter models
  const [dynamicOpenRouterModels, setDynamicOpenRouterModels] = useState({});
  const [loadingModels, setLoadingModels] = useState(false);
  const [modelContextLengths, setModelContextLengths] = useState({});
  const [openAIModels, setOpenAIModels] = useState([
    { value: "o1", label: "o1 (Reasoning model for hard problems)" },
    { value: "o1-mini", label: "o1-Mini (Fast, cheap reasoning model for coding, math, science)" },
    { value: "o3-mini", label: "o3-Mini (Fast, cheap reasoning model for coding, math, science)" },
    { value: "o3-mini-high", label: "o3-Mini-high (Fast reasoning model for coding, math, science)" },
    { value: "gpt-4o", label: "GPT-4o (High-intelligence flagship model)" },
    { value: "gpt-4o-mini", label: "GPT-4o-mini (Affordable, intelligent small model)" },
  ]);

  const nodes = useNodes(); 

  // Fetch OpenRouter models on component mount
  useEffect(() => {
    fetchOpenRouterModels();
  }, []);

  useEffect(() => {
    data.updateNodeData(id, { 
      nodeName, 
      imageUrls, 
      settings, 
      content, 
      openaiTools, 
      handleSettingChange, 
      handleContentChange, 
      handleModelChange 
    });
  }, [nodeName, imageUrls, settings, content, openaiTools, id, data]);

  useEffect(() => {
    if (openaiTools?.fileSearch?.enabled) {
        fetchVectorStores();
    }
  }, [openaiTools?.fileSearch?.enabled]);

  // Function to fetch models from OpenRouter API through WordPress backend
  const fetchOpenRouterModels = async () => {
    if (data.isViewer) {
      return;
    }
    
    setLoadingModels(true);
    try {
      const response = await axios.get(`${config.apiSettings.root}wp-ai-workflows/v1/openrouter-models`, {
        headers: {
          'X-WP-Nonce': config.apiSettings.nonce
        }
      });
      
      if (response.data && Array.isArray(response.data.data)) {
        // Process the models and group by provider
        const models = response.data.data;
        const groupedModels = {};
        const contextLengths = {};
        
        // Create a dynamic list for direct OpenAI models
        const dynamicOpenAIModels = [];
        
        models.forEach(model => {
          // Extract provider from model ID (e.g., "anthropic/claude-3" -> "Anthropic")
          const providerMatch = model.id.match(/^([^/]+)\//);
          const provider = providerMatch ? 
            providerMatch[1].charAt(0).toUpperCase() + providerMatch[1].slice(1) : 
            "Other";
          
          if (!groupedModels[provider]) {
            groupedModels[provider] = [];
          }
          
          // Store context length for the model
          contextLengths[model.id] = model.context_length || 4096;
          
          // If it's an OpenAI model, also add it to the direct models list
          if (provider.toLowerCase() === 'openai') {
            const directModelId = model.id.split('/')[1]; // Remove the "openai/" prefix
            const directModelExists = dynamicOpenAIModels.some(m => m.value === directModelId);
            
            // Only add if it's not already in the list
            if (!directModelExists) {
              dynamicOpenAIModels.push({
                value: directModelId,
                label: `${model.name || directModelId} (${model.description || 'OpenAI model'})`,
                description: model.description || ''
              });
              
              // Also store context length for direct model
              contextLengths[directModelId] = model.context_length || 4096;
            }
          }
          
          groupedModels[provider].push({
            value: model.id,
            label: model.name || model.id.split('/').pop(),
            description: model.description || ''
          });
        });
        
        // Sort models within each provider
        Object.keys(groupedModels).forEach(provider => {
          groupedModels[provider].sort((a, b) => a.label.localeCompare(b.label));
        });
        
        // Sort direct OpenAI models
        dynamicOpenAIModels.sort((a, b) => a.label.localeCompare(b.label));
        
        setDynamicOpenRouterModels(groupedModels);
        setModelContextLengths(contextLengths);
        
        // Replace the hardcoded OpenAI models with the dynamic ones if we have any
        if (dynamicOpenAIModels.length > 0) {
          setOpenAIModels(dynamicOpenAIModels);
        }
      }
    } catch (error) {
      console.error('Error fetching OpenRouter models:', error);
      message.error('Failed to fetch AI models from OpenRouter');
      
      // Fallback to hardcoded models
      setDynamicOpenRouterModels(openRouterModels);
    } finally {
      setLoadingModels(false);
    }
  };

  const getModelDisplayName = () => {
    const model = data.model || 'gpt-4o-mini';
    
    // Check OpenAI direct models
    const openAIModel = openAIModels.find(m => m.value === model);
    if (openAIModel) {
      return openAIModel.label.split(' (')[0]; // Remove description in parentheses
    }
    
    // Check dynamic OpenRouter models
    for (const provider in dynamicOpenRouterModels) {
      const foundModel = dynamicOpenRouterModels[provider].find(m => m.value === model);
      if (foundModel) {
        return foundModel.label;
      }
    }
    
    // Check hardcoded OpenRouter models (fallback)
    for (const provider in openRouterModels) {
      const foundModel = openRouterModels[provider].find(m => m.value === model);
      if (foundModel) {
        return foundModel.label;
      }
    }
    
    return model; // Fallback to model ID if not found
  };

  const handleModelChange = (value) => {
    data.updateNodeData(id, { model: value });
    // Update max_tokens based on the selected model
    const maxTokens = getMaxTokensForModel(value);
    setSettings(prev => ({ ...prev, max_tokens: maxTokens }));
  };

  const isDirectOpenAIModel = (model) => {
    return openAIModels.some(m => m.value === model);
  };

  const fetchVectorStores = async () => {
    if (data.isViewer) {
      return;
    }
    
    try {
      setFetchingVectorStores(true);
      
      // Fetch managed vector stores
      const managedResponse = await axios.get(`${config.apiSettings.root}wp-ai-workflows/v1/vector-stores`, {
        headers: {
          'X-WP-Nonce': config.apiSettings.nonce
        }
      });
      
      // Fetch OpenAI vector stores
      const openaiResponse = await axios.get(`${config.apiSettings.root}wp-ai-workflows/v1/openai-vector-stores`, {
        headers: {
          'X-WP-Nonce': config.apiSettings.nonce
        }
      });
      
      // Process OpenAI stores
      let openaiStores = [];
      if (openaiResponse.data && openaiResponse.data.data) {
        openaiStores = openaiResponse.data.data.map(store => ({
          id: store.id,
          name: `${store.name} (OpenAI)`,
          is_default: false,
          is_openai_store: true
        }));
      }
      
      // Combine the two sets of stores
      const combinedStores = [...managedResponse.data, ...openaiStores];
      setVectorStores(combinedStores);
    } catch (error) {
      console.error('Error fetching vector stores:', error);
      message.error('Failed to fetch vector stores');
    } finally {
      setFetchingVectorStores(false);
    }
  };

  const handleToolToggle = (toolType, enabled) => {
      setOpenaiTools(prev => ({
          ...prev,
          [toolType]: {
              ...prev[toolType],
              enabled
          }
      }));
  };

  const handleToolOptionChange = (toolType, option, value) => {
      setOpenaiTools(prev => ({
          ...prev,
          [toolType]: {
              ...prev[toolType],
              [option]: value
          }
      }));
  };

  const handleToolLocationChange = (locationKey, value) => {
      setOpenaiTools(prev => ({
          ...prev,
          webSearch: {
              ...prev.webSearch,
              location: {
                  ...prev.webSearch.location,
                  [locationKey]: value
              }
          }
      }));
  };

  const getMaxTokensForModel = (model) => {
    // Check if we have context length from dynamically fetched models
    if (modelContextLengths[model]) {
      return modelContextLengths[model];
    }
    
    // Fallback to hardcoded values
    const tokenLimits = {
      'gpt-4o': 16384,
      'gpt-4o-mini': 16384,
      'o1': 100000,
      'o1-mini': 65536,
      'o3-mini': 100000,
      'o3-mini-high': 100000,
      'meta-llama/llama-3.2-11b-vision-instruct:free': 4096,
      'meta-llama/llama-3.2-11b-vision-instruct': 131072,
      'meta-llama/llama-3.3-70b-instruct': 131072,
      'mistralai/mistral-nemo': 128000,
      'mistralai/mistral-large-2411': 128000,
      'mistralai/pixtral-large-2411': 128000,
      'mistralai/mistral-small-3.1-24b-instruct': 128000,
      'google/gemma-2-9b-it:free': 2048,
      'anthropic/claude-3.7-sonnet': 128000,
      'anthropic/claude-3.5-sonnet': 8192,
      'anthropic/claude-3-5-haiku': 8192,
      'anthropic/claude-3-haiku': 4096,
      'anthropic/claude-3-opus': 4096,
      'openai/gpt-4o-search-preview': 16384,
      'openai/gpt-4o-mini-search-preview': 16384,
      'openai/chatgpt-4o-latest': 16384,
      'openai/gpt-4o-mini': 16384,
      'openai/o1': 100000,
      'openai/o1-mini': 65536,
      'openai/o3-mini': 100000,
      'openai/o3-mini-high': 100000,
      'google/gemma-3-12b-it:free': 8192,
      'google/gemma-3-4b-it:free': 8192,
      'google/gemini-flash-8b-1.5-exp': 8192,
      'google/gemini-flash-1.5-8b': 8192,
      'google/gemini-2.0-flash-exp': 8192,
      'x-ai/grok-2-vision-1212': 32768,
      'x-ai/grok-2-1212': 131072,
      'perplexity/sonar': 127072,
      'perplexity/sonar-reasoning': 127072,
      'deepseek/deepseek-r1': 128000,
      'deepseek/deepseek-chat': 131000, 
    };
    return tokenLimits[model] || 4096; 
  };

  const handleNameChange = (e) => {
    const newName = e.target.value;
    setNodeName(newName);
  };

  const handleContentChange = (nodeId, newData) => {
    setContent(newData.content); // Add this for immediate UI update
    data.updateNodeData(nodeId, newData);
  };

  const handleAddImage = () => {
    setImageUrls([...imageUrls, '']);
  };

  const handleImageUrlChange = (index, value) => {
    const newUrls = [...imageUrls];
    newUrls[index] = value;
    setImageUrls(newUrls);
  };

  const handleRemoveImage = (index) => {
    const newUrls = imageUrls.filter((_, i) => i !== index);
    setImageUrls(newUrls);
  };

  const handleInsertImageTag = (index, tag) => {
    const newUrls = [...imageUrls];
    newUrls[index] = newUrls[index] ? newUrls[index] + tag : tag;
    setImageUrls(newUrls);
  };

  const handleSettingChange = (setting, value) => {
    console.log('Setting change triggered:', { setting, value });
    setSettings(prev => ({
      ...prev,
      [setting]: value
    }));
  };

  // Keeping the hardcoded models as fallback
  const openRouterModels = {
    OpenAI: [
      { value: "openai/o1", label: "o1" },
      { value: "openai/o1-mini", label: "o1-Mini" },
      { value: "openai/o3-mini-high", label: "o3 mini high" },
      { value: "openai/o3-mini", label: "o3 mini" },
      { value: "openai/chatgpt-4o-latest", label: "ChatGPT-4o Latest" },
      { value: "openai/gpt-4o-mini", label: "GPT-4o Mini" },
      { value: "openai/gpt-4o-search-preview", label: "GPT-4o with Search (Preview)" },
      { value: "openai/gpt-4o-mini-search-preview", label: "GPT-4o Mini Search (Preview)" },
    ],
    Anthropic: [
      { value: "anthropic/claude-3.7-sonnet", label: "Claude 3.7 Sonnet" },
      { value: "anthropic/claude-3.5-sonnet", label: "Claude 3.5 Sonnet" },
      { value: "anthropic/claude-3-5-haiku", label: "Claude 3.5 Haiku" },
      { value: "anthropic/claude-3-opus", label: "Claude 3 Opus" },
      { value: "anthropic/claude-3-haiku", label: "Claude 3 Haiku" },
    ],
    "Meta Llama": [
      { value: "meta-llama/llama-3.3-70b-instruct", label: "Llama 3.3 70B Instruct" },
      { value: "meta-llama/llama-3.2-3b-instruct", label: "Llama 3.2 3B Instruct" },
      { value: "meta-llama/llama-3.2-1b-instruct", label: "Llama 3.2 1B Instruct" },
      { value: "meta-llama/llama-3.2-90b-vision-instruct", label: "Llama 3.2 90B Vision Instruct" },
      { value: "meta-llama/llama-3.2-11b-vision-instruct:free", label: "Llama 3.2 11B Vision Instruct (Free)" },
    ],
    Google: [
      { value: "google/gemma-3-12b-it:free", label: "Gemma 3 12B (Free)" },
      { value: "google/gemma-3-4b-it:free", label: "Gemma 3 4B (Free)" },
      { value: "google/gemini-2.0-flash-exp:free", label: "Gemini 2.0 Flash Experimental (free)" },
      { value: "google/gemini-flash-8b-1.5-exp", label: "Gemini Flash 8B 1.5 Exp" },
      { value: "google/gemini-flash-1.5-8b", label: "Gemini Flash 1.5" },
      { value: "google/gemma-2-9b-it:free", label: "Gemma 2 9B IT (Free)" },
    ],
    Perplexity: [
      { value: "perplexity/sonar", label: "Perplexity Sonar" },
      { value: "perplexity/sonar-reasoning", label: "Perplexity Sonar Reasoning" },
    ],
    Mistral: [
      { value: "mistralai/mistral-small-3.1-24b-instruct", label: "Mistral Small 3.1 24B" },
      { value: "mistralai/pixtral-large-2411", label: "Pixtral 2" },
      { value: "mistralai/mistral-large-2411", label: "Mistral Large 2" },
      { value: "mistralai/mistral-nemo", label: "Mistral Nemo" },
    ],
    xAI: [
      { value: "x-ai/grok-2-1212", label: "Grok 2" },
      { value: "x-ai/grok-2-vision-1212", label: "Grok 2 Vision" },
    ],
    DeepSeek: [
      { value: "deepseek/deepseek-r1", label: "DeepSeek R1" },
      { value: "deepseek/deepseek-chat", label: "DeepSeek V3" },
    ]
  };

  const getModelIcon = (provider) => {
    const iconSize = 20; // Standard size for all icons
    const normalizedProvider = provider.toLowerCase();
    
    switch (normalizedProvider) {
      case 'openai':
        return <OpenAI size={iconSize} />;
      case 'google':
        return <Gemini size={iconSize} />;
      case 'perplexity':
        return <Perplexity size={iconSize} />;
      case 'mistral':
      case 'mistralai':
        return <Mistral size={iconSize} />;
      case 'meta':
      case 'meta-llama':
      case 'meta llama':
        return <Meta size={iconSize} />;
      case 'anthropic':
        return <Anthropic size={iconSize} />;
      case 'xai':
      case 'x-ai':
      case 'x':
        return <Grok size={iconSize} />;
      case 'deepseek':
        return <DeepSeek size={iconSize} />;
      case 'qwen':
      case 'alibaba':
      case 'aliqwen':
        return <Qwen size={iconSize} />;
      case 'cohere':
        return <Cohere size={iconSize} />;
      case 'nvidia':
        return <Nvidia size={iconSize} />;
      case 'nova':
      case 'anthropic nova':
      case 'novaai':
        return <Nova size={iconSize} />;
      case 'microsoft':
      case 'azure':
        return <WindowsFilled size={iconSize} />;
      case 'moonshot':
      case 'moonshot-ai':
        return <Moonshot size={iconSize} />;
      case 'openrouter':
        return <OpenRouter size={iconSize} />;
      default:
        return <RobotOutlined style={{ fontSize: iconSize }} />;
    }
  };

  const getMinValue = (setting) => {
    if (setting === 'max_tokens') return 1;
    switch (setting) {
      case 'top_k':
        return 0;
      case 'frequency_penalty':
      case 'presence_penalty':
        return -2;
      case 'repetition_penalty':
      case 'top_p':
      case 'temperature':
        return 0;
      default:
        return 0;
    }
  };
  
  const getMaxValue = (setting) => {
    if (setting === 'max_tokens') return getMaxTokensForModel(data.model);
    switch (setting) {
      case 'top_p':
        return 1;
      case 'temperature':
      case 'frequency_penalty':
      case 'presence_penalty':
      case 'repetition_penalty':
        return 2;
      case 'top_k':
        return Number.MAX_SAFE_INTEGER;
      default:
        return 1;
    }
  };

  const getRangeText = (setting) => {
    if (setting === 'max_tokens') return `(1 to ${getMaxTokensForModel(data.model)})`;
    switch (setting) {
      case 'temperature':
        return '(0.0 to 2.0)';
      case 'top_p':
        return '(0.0 to 1.0)';
      case 'top_k':
        return '(0 or above)';
      case 'frequency_penalty':
      case 'presence_penalty':
        return '(-2.0 to 2.0)';
      case 'repetition_penalty':
        return '(0.0 to 2.0)';
      default:
        return '';
    }
  };

  return (
    <Card 
      title={
        <Space>
          <RobotOutlined style={{ fontSize: '18px', color: '#1890ff' }}/>
          <span>AI Model</span>
          <Tag color="blue">
            {getModelDisplayName()}
          </Tag>
        </Space>
      }
      style={{ width: 380 }}
      extra={
        <Space>
          <Button 
            icon={<SettingOutlined />}
            type="text"
            onClick={() => setActiveTab('settings')}
          />
      <Popconfirm
        title="Delete Node"
        description="Are you sure you want to delete this node?"
        onConfirm={() => data.onDelete(id)}
        okText="Delete"
        cancelText="Cancel"
        okButtonProps={{ danger: true }} > 
        <Button icon={<DeleteOutlined />} /> </Popconfirm>
        </Space>
      }
    >
      <Handle type="target" position={Position.Left} isConnectable={isConnectable} />
      
      <Input
        placeholder="Node Name"
        value={nodeName}
        onChange={(e) => setNodeName(e.target.value)}
        style={{ marginBottom: 16 }}
        prefix={<InfoCircleOutlined style={{ color: '#bfbfbf' }} />}
        className="nodrag"
      />

      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane tab="Model" key="model">
          <Space direction="vertical" style={{ width: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <Text strong>Select AI Model</Text>
              <Button 
                icon={<ReloadOutlined />} 
                size="small"
                onClick={fetchOpenRouterModels}
                loading={loadingModels}
                type="text"
              />
            </div>
            
            <Select
              className="nodrag"
              style={{ width: '100%', marginBottom: 8 }}
              value={data.model || 'gpt-4o-mini'}
              onChange={handleModelChange}
              onClick={(e) => e.stopPropagation()}
              showSearch
              filterOption={(input, option) => {
                // Safely handle string or object labels
                const searchText = option.label || option['data-label'] || '';
                const searchValue = typeof searchText === 'string' ? searchText : '';
                return searchValue.toLowerCase().includes(input.toLowerCase());
              }}
              loading={loadingModels}
              placeholder={loadingModels ? "Loading models..." : "Select AI model"}
              notFoundContent={loadingModels ? <Spin size="small" /> : null}
            >
              <OptGroup label="OpenAI (Direct)">
                {openAIModels.map(model => (
                  <Option key={model.value} value={model.value} label={model.label} data-label={model.label}>
                    <Space>
                      <OpenAI size={20} />
                      <Text>{model.label}</Text>
                    </Space>
                  </Option>
                ))}
              </OptGroup>
              
              {/* Dynamic OpenRouter Models */}
              {Object.keys(dynamicOpenRouterModels).length > 0 ? (
                Object.entries(dynamicOpenRouterModels).map(([provider, models]) => (
                  <OptGroup 
                    key={provider} 
                    label={
                      <Space>
                        {getModelIcon(provider)}
                        <span>{provider} (via OpenRouter)</span>
                      </Space>
                    }
                  >
                    {models.map(model => (
                      <Option key={model.value} value={model.value} label={model.label} data-label={model.label}>
                        <Space>
                          {getModelIcon(provider)}
                          <Text>{model.label}</Text>
                          {model.description && (
                            <Tooltip title={model.description}>
                              <InfoCircleOutlined style={{ color: '#bfbfbf' }} />
                            </Tooltip>
                          )}
                        </Space>
                      </Option>
                    ))}
                  </OptGroup>
                ))
              ) : (
                // Fallback to hardcoded models if dynamic fetch fails
                Object.entries(openRouterModels).map(([provider, models]) => (
                  <OptGroup 
                    key={provider} 
                    label={
                      <Space>
                        {getModelIcon(provider)}
                        <span>{provider} (via OpenRouter)</span>
                      </Space>
                    }
                  >
                    {models.map(model => (
                      <Option key={model.value} value={model.value} label={model.label}>
                        <Space>
                          {getModelIcon(provider)}
                          <Text>{model.label}</Text>
                        </Space>
                      </Option>
                    ))}
                  </OptGroup>
                ))
              )}
            </Select>

            {/* Show tools only for OpenAI models */}
            {isDirectOpenAIModel(data.model) && (
                <div style={{ 
                    padding: '12px',
                    background: '#f5f5f5',
                    borderRadius: '6px',
                    marginBottom: 16
                }}>
                    <Space direction="vertical" style={{ width: '100%' }}>
                        <Text strong>OpenAI Tools</Text>
                        
                        <Checkbox 
                            checked={openaiTools?.webSearch?.enabled || false}
                            onChange={(e) => handleToolToggle('webSearch', e.target.checked)}
                        >
                            Enable Web Search
                        </Checkbox>
                        
                        {openaiTools?.webSearch?.enabled && (
                            <div style={{ marginLeft: 24 }}>
                                <Text>Search Context Size</Text>
                                <Select
                                    size="small"
                                    className="nodrag"
                                    style={{ width: '100%', marginTop: 4 }}
                                    value={openaiTools?.webSearch?.contextSize || 'medium'}
                                    onChange={(value) => handleToolOptionChange('webSearch', 'contextSize', value)}
                                >
                                    <Option value="low">Low (fastest, cheapest)</Option>
                                    <Option value="medium">Medium (balanced)</Option>
                                    <Option value="high">High (most comprehensive)</Option>
                                </Select>
                                
                                <Collapse ghost style={{ marginTop: 8 }}>
                                    <Panel header="Location Settings (Optional)" key="location">
                                        <Space direction="vertical" style={{ width: '100%' }}>
                                            <Input 
                                                size="small"
                                                placeholder="City"
                                                value={openaiTools?.webSearch?.location?.city || ''}
                                                onChange={(e) => handleToolLocationChange('city', e.target.value)}
                                            />
                                            <Input 
                                                size="small"
                                                placeholder="Region/State"
                                                value={openaiTools?.webSearch?.location?.region || ''}
                                                onChange={(e) => handleToolLocationChange('region', e.target.value)}
                                            />
                                            <Input 
                                                size="small"
                                                placeholder="Country Code"
                                                value={openaiTools?.webSearch?.location?.country || ''}
                                                onChange={(e) => handleToolLocationChange('country', e.target.value)}
                                                maxLength={2}
                                            />
                                        </Space>
                                    </Panel>
                                </Collapse>
                            </div>
                        )}
                        
                        <Checkbox 
                            checked={openaiTools?.fileSearch?.enabled || false}
                            onChange={(e) => handleToolToggle('fileSearch', e.target.checked)}
                        >
                            Enable File Search
                        </Checkbox>
                        
                        {openaiTools?.fileSearch?.enabled && (
                            <div style={{ marginLeft: 24 }}>
                                <Text>Vector Store</Text>
                                <Select
                                    size="small"
                                    className="nodrag"
                                    style={{ width: '100%', marginTop: 4 }}
                                    value={openaiTools?.fileSearch?.vectorStoreId || ''}
                                    onChange={(value) => handleToolOptionChange('fileSearch', 'vectorStoreId', value)}
                                    placeholder="Select a vector store"
                                    loading={fetchingVectorStores}
                                >
                                    {vectorStores.map(store => (
                                        <Option key={store.id} value={store.id}>
                                            {store.name}
                                        </Option>
                                    ))}
                                </Select>
                                
                                <Text>Max Results</Text>
                                <InputNumber
                                    size="small"
                                    min={1}
                                    max={20}
                                    style={{ width: '100%', marginTop: 4 }}
                                    value={openaiTools?.fileSearch?.maxResults || 5}
                                    onChange={(value) => handleToolOptionChange('fileSearch', 'maxResults', value)}
                                />
                            </div>
                        )}
                    </Space>
                </div>
            )}

            <div style={{ marginTop: 8 }}>
                <Space align="center" style={{ marginBottom: 4 }}>
                    <Text strong>Prompt</Text>
                    <Tooltip title="Enter your prompt for the AI model">
                        <InfoCircleOutlined style={{ color: '#bfbfbf' }} />
                    </Tooltip>
                </Space>
                <GenericNodeContent 
                    id={id} 
                    data={data} 
                    onChange={handleContentChange}
                    placeholder="Enter your prompt here..."
                    nodes={nodes}
                    gravityFormFields={data.selectedFields}
                />
            </div>
          </Space>
        </TabPane>

        <TabPane 
          tab={
            <Space>
              <PictureOutlined />
              <span>Images</span>
            </Space>
          } 
          key="images"
        >
          <Space direction="vertical" style={{ width: '100%' }}>
          {imageUrls.map((url, index) => (
            <div 
              key={index} 
              style={{ 
                padding: '8px',
                background: '#f5f5f5',
                borderRadius: '6px',
                marginBottom: 8
              }}
            >
              <div style={{ marginBottom: 8 }}>
                <Input
                  value={url}
                  onChange={(e) => handleImageUrlChange(index, e.target.value)}
                  placeholder="Enter image URL or connect from previous node"
                  style={{ width: '100%' }}
                  className="nodrag"
                />
              </div>
              <div style={{ 
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <NodeInputHandler
                  nodeId={id}
                  onInsert={(tag) => handleInsertImageTag(index, tag)}
                />
                <Button 
                  type="text" 
                  icon={<MinusCircleOutlined />} 
                  onClick={() => handleRemoveImage(index)}
                  danger
                />
              </div>
            </div>
          ))}
            <Button 
              type="dashed" 
              onClick={handleAddImage} 
              block 
              icon={<PlusOutlined />}
            >
              Add Image Input
            </Button>
          </Space>
        </TabPane>

        <TabPane tab="Settings" key="settings">
          <Space direction="vertical" style={{ width: '100%' }}>
            {Object.entries(settings).map(([key, value]) => (
              <div key={key} style={{ marginBottom: 16 }}>
                <Space align="center">
                  <Text strong>{key.replace('_', ' ').charAt(0).toUpperCase() + key.replace('_', ' ').slice(1)}</Text>
                  <Tooltip title={getSettingExplanation(key)}>
                    <InfoCircleOutlined style={{ color: '#bfbfbf' }} />
                  </Tooltip>
                </Space>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  gap: 8,
                  marginTop: 4,
                  padding: 8,
                  background: '#f5f5f5',
                  borderRadius: 6
                }}>
                  <InputNumber
                    value={value}
                    onChange={(newValue) => handleSettingChange(key, newValue)}
                    step={key === 'max_tokens' ? 1 : 0.1}
                    min={getMinValue(key)}
                    max={key === 'max_tokens' ? getMaxTokensForModel(data.model) : getMaxValue(key)}
                    style={{ width: 120 }}
                  />
                  <Text type="secondary">{getRangeText(key)}</Text>
                </div>
              </div>
            ))}
          </Space>
        </TabPane>
      </Tabs>

      <Handle type="source" position={Position.Right} id="a" isConnectable={isConnectable} />
    </Card>
  );
};


const getSettingExplanation = (setting) => {
  const explanations = {
    temperature: "Influences response variety. Lower values (0.0) give predictable responses, higher values (up to 2.0) encourage diversity.",
    top_p: "Limits token choices to a percentage of likely tokens. Lower values increase predictability, default (1.0) allows full range.",
    top_k: "Limits token choices at each step. Lower values increase predictability, 0 (default) considers all choices.",
    frequency_penalty: "Controls token repetition based on input frequency. Positive values reduce frequent token use, negative values encourage it.",
    presence_penalty: "Adjusts repetition of specific input tokens. Positive values reduce repetition, negative values encourage it.",
    repetition_penalty: "Reduces input token repetition. Higher values (up to 2.0) decrease repetition, but may affect coherence.",
    max_tokens: "Suggests a preferred maximum length for the model's response. This is a guideline only - the AI provider may adjust this based on their own limits and capabilities.",
  };
  return explanations[setting] || "No explanation available.";
};

export default AIModelNode;